import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import Container from '../../components/layout/Container/Container';
import Card, { CardBody } from '../../components/ui/Card/Card';
import Badge from '../../components/ui/Badge/Badge';
import { db } from '../../services/firebase';
import './Blog.css';

const formatDate = (timestamp) => {
  if (!timestamp || typeof timestamp.toDate !== 'function') return '';
  return timestamp.toDate().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const postsQuery = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'));

    const unsubscribe = onSnapshot(
      postsQuery,
      (snapshot) => {
        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter((post) => post.isPublished === true);
        setPosts(data);
        setError('');
        setLoading(false);
      },
      (err) => {
        if (err?.code === 'permission-denied') {
          setError('Firestore denied blog access. Check your read rules for the posts collection.');
        } else {
          setError('Unable to load blog posts right now.');
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <div className="blog-page bg-light">
      <Container>
        <div className="blog-header text-center mb-5">
          <p className="text-uppercase text-muted small mb-2">Blog</p>
          <h1 className="display-6 fw-bold mb-3">Latest Stories</h1>
          <p className="text-muted mb-0">Thoughts, updates, and insights from the studio.</p>
        </div>

        {loading && <p className="text-center text-muted">Loading posts...</p>}
        {error && <p className="text-center text-danger">{error}</p>}

        {!loading && !error && posts.length === 0 && (
          <p className="text-center text-muted">No posts published yet.</p>
        )}

        <div className="blog-grid">
          {posts.map((post) => (
            <Card key={post.id} className="blog-card shadow-sm border-0">
              {post.coverImage && (
                <div className="blog-card-image">
                  <img src={post.coverImage} alt={post.title} />
                </div>
              )}
              <CardBody className="blog-card-body">
                <div className="blog-meta d-flex flex-wrap align-items-center gap-2 mb-3">
                  {post.publishedAt && (
                    <span className="text-muted small">{formatDate(post.publishedAt)}</span>
                  )}
                  {post.tags?.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {post.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="light" className="text-uppercase small">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <h2 className="h4 fw-bold blog-post-title">{post.title}</h2>
                <p className="text-muted blog-post-excerpt">{post.excerpt}</p>
                <div className="blog-content text-muted">{post.content}</div>
              </CardBody>
            </Card>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default Blog;
